









































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { PartnerDialogResult } from "@/components/admin/clients/partners/ClientPartnerFieldsDialog.vue";
import { ClientPartner, MAX_PARTNER_CONTACTS, PARTNER_CONTACT_ROLES } from "@/data/models/ClientPartner";
import { updateClientPartner } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  PartnerContactForm,
  partnerContactChanges,
  partnerContactErrors,
  partnerContactForms,
  partnerErrorKey,
} from "@/utils/ClientPartners";

const PARTNERS = "admin.clients.partners";

/**
 * Edits a system's Kontaktpersonen: the partner's own people, each with a name, a role (Technik / Fachlich)
 * and optionally an e-mail address and a phone number. The whole list is sent with the list the card
 * showed as `expected`, so IRES Cloud refuses it when someone changed it meanwhile.
 */
@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class ClientPartnerContactsDialog extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;
  clientId = "";
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  partner: ClientPartner | null = null;
  forms: PartnerContactForm[] = [];
  /** The rows whose name field was left: a new row's empty name is not an error until then. */
  leftNames: PartnerContactForm[] = [];

  readonly maxContacts = MAX_PARTNER_CONTACTS;

  private resolve: ((result: PartnerDialogResult) => void) | null = null;

  get roleItems() {
    return PARTNER_CONTACT_ROLES.map((value) => ({ value, label: this.$t(`${PARTNERS}.contacts.roles.${value}`).toString() }));
  }

  get changes() {
    // Read the forms first, so the computed tracks them even before a partner is shown (ui-conventions.md).
    const forms = this.forms.map((form) => ({ ...form }));
    return this.partner ? partnerContactChanges(this.partner.contacts, forms) : {};
  }

  get hasChanged(): boolean {
    return Object.keys(this.changes).length > 0;
  }

  get canSave(): boolean {
    return this.hasChanged && this.forms.every((form) => partnerContactErrors(form).length === 0);
  }

  /**
   * A field's message: a missing name once its field was left; an address or a phone number only when
   * typed and not valid. SICHERN stays disabled either way.
   */
  errorOf(form: PartnerContactForm, field: "name" | "email" | "phone"): string {
    if (!partnerContactErrors(form).includes(field) || (field === "name" && !this.leftNames.includes(form))) {
      return "";
    }
    return this.$t(field === "name" ? "admin.clients.masterData.fieldErrors.required" : `${PARTNERS}.contacts.invalid.${field}`).toString();
  }

  onNameLeft(form: PartnerContactForm) {
    if (!this.leftNames.includes(form)) {
      this.leftNames.push(form);
    }
  }

  onAdd() {
    this.forms.push({ name: "", role: "TECHNICAL", email: "", phone: "" });
  }

  /**
   * @param clientId - the system's owner, whose page this is.
   * @param partner - the system as the page loaded it.
   */
  show(clientId: string, partner: ClientPartner): Promise<PartnerDialogResult> {
    this.clientId = clientId;
    this.partner = partner;
    this.forms = partnerContactForms(partner.contacts);
    this.leftNames = [];
    this.baseDialog.show({
      title: this.$t(`${PARTNERS}.editTitles.PARTNER_CONTACTS`, [partner.name]).toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 760,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onSave() {
    if (!this.canSave || !this.partner) {
      return;
    }
    this.busy = true;
    try {
      const saved = await updateClientPartner(this.clientId, this.partner.id, "PARTNER_CONTACTS", this.changes);
      SnackbarModule.showSuccess({ key: `${PARTNERS}.saved` });
      this.close(saved);
    } catch (error) {
      const key = partnerErrorKey(error);
      SnackbarModule.showError({ key });
      if (key.endsWith("CHANGED_MEANWHILE")) {
        this.close("CHANGED_MEANWHILE");
      }
    } finally {
      this.busy = false;
    }
  }

  close(result: PartnerDialogResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: PartnerDialogResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
